/**
 * CC Management drawer — platform-staff-only tools (2026-06 nav
 * refresh follow-up). Renders nothing unless the logged-in user is
 * Club Central platform staff (central-auth user whose central role
 * isn't "Tenant Owner" — see utils/platformStaff for why that's the
 * gate). Customers' tenant owners and tenant staff never see it.
 *
 * Deliberately styled in platform slate, not the tenant brand: when
 * this drawer is open you are operating AS Club Central inside a
 * customer's tenant, and the styling should say so.
 *
 * Trigger: "Club Central tools" in the account menu (top bar), shown only
 * to platform staff (Dan 2026-10-31; it was a "CC" pill in the top bar).
 */
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  X,
  ExternalLink,
  Activity,
  HeartPulse,
  MailCheck,
  MessageSquareWarning,
  OctagonPause,
  ScrollText,
  BookOpen,
  ClipboardCheck,
  Building2,
  ArrowLeftRight,
  Check,
} from "lucide-react";
import { useAppVersion } from "../../hooks/useAppVersion";
import apiConfig from "../../api/config";
import { isPlatformStaff, isSuperAdmin } from "../../utils/platformStaff";
import { getMyTenants, MyTenant } from "../../api/authAPI";

interface CCManagementDrawerProps {
  /** opened from the account menu's "Club Central tools" item */
  open: boolean;
  onClose: () => void;
}

const CCManagementDrawer: React.FC<CCManagementDrawerProps> = ({ open, onClose }) => {
  const location = useLocation();
  const tenantId = location.pathname.split("/").filter(Boolean)[0] || "";
  // null = not loaded yet (lazy-fetched the first time the drawer opens)
  const [tenants, setTenants] = useState<MyTenant[] | null>(null);
  const { currentVersion } = useAppVersion();

  // CC platform staff (central auth, central role ≠ Tenant Owner) —
  // see utils/platformStaff for why authSource alone is not enough.
  const isCCStaff = isPlatformStaff();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Lazy-load the switchable tenant list the first time the drawer opens.
  useEffect(() => {
    if (!open || tenants !== null) return;
    let cancelled = false;
    getMyTenants().then((list) => {
      if (!cancelled) setTenants(list);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!isCCStaff) return null;

  // Hard navigation on switch: tenant-scoped state (settings cache,
  // tenant supabase client, Stripe client) must be rebuilt from the new
  // slug — a client-side route change would leak the old tenant's
  // state. Dropping the settings cache also prevents the old tenant's
  // branding flashing on the new tenant's login/load.
  const switchTenant = (slug: string) => {
    if (slug === tenantId) {
      onClose();
      return;
    }
    localStorage.setItem("tenantSlug", slug);
    localStorage.removeItem("settings");
    window.location.assign(`/${slug}/manage/dashboard`);
  };

  const businessName = (() => {
    try {
      return JSON.parse(localStorage.getItem("settings") || "{}")?.business_name || tenantId;
    } catch {
      return tenantId;
    }
  })();

  const tenantTools: Array<{ to: string; label: string; icon: React.ReactNode; hint?: string }> = [
    {
      to: `/${tenantId}/manage/data-health`,
      label: "Data Health",
      icon: <HeartPulse className="w-4 h-4" />,
      hint: "Automated integrity checks",
    },
    {
      to: `/${tenantId}/manage/config-review/settings_general`,
      label: "Config Review",
      icon: <ClipboardCheck className="w-4 h-4" />,
      hint: "Walk the tenant's configuration",
    },
    {
      to: `/${tenantId}/manage/reports/email-verification`,
      label: "Email Verification",
      icon: <MailCheck className="w-4 h-4" />,
      hint: "Bulk-verify (consumes MailerSend credit)",
    },
    {
      to: `/${tenantId}/manage/settings/sms-diagnostics`,
      label: "SMS Diagnostics",
      icon: <MessageSquareWarning className="w-4 h-4" />,
      hint: "Delivery receipts + opt-in state",
    },
    {
      to: `/${tenantId}/manage/settings/stripe-killswitch`,
      label: "Stripe Killswitch",
      icon: <OctagonPause className="w-4 h-4" />,
      hint: "Emergency payment shutoff",
    },
    {
      to: `/${tenantId}/manage/activity`,
      label: "Activity Log",
      icon: <ScrollText className="w-4 h-4" />,
    },
    {
      to: `/${tenantId}/manage/knowledgebase`,
      label: "Knowledgebase",
      icon: <BookOpen className="w-4 h-4" />,
    },
  ];

  const rowCls =
    "flex items-center gap-3 px-3 py-2 rounded-md text-sm text-slate-200 hover:bg-slate-700/70 transition-colors";

  return (
    <>
      {open && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black/40 z-[90]"
            onClick={() => onClose()}
          />
          {/* Drawer */}
          <div
            className="fixed top-0 right-0 h-full w-80 z-[100] bg-slate-900 text-slate-100 shadow-2xl border-l border-slate-700 flex flex-col"
            role="dialog"
            aria-label="CC management tools"
          >
            <div className="px-4 py-3 border-b border-slate-700 flex items-start justify-between">
              <div>
                <div className="text-sm font-bold tracking-wide flex items-center gap-2">
                  Club Central
                  {isSuperAdmin() && (
                    <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                      Super Admin
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-400">Platform tools</div>
              </div>
              <button
                onClick={() => onClose()}
                className="p-1.5 rounded-md hover:bg-slate-700 transition-colors"
                aria-label="Close CC management tools"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Tenant context */}
            <div className="px-4 py-3 border-b border-slate-700/70 bg-slate-800/50">
              <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-0.5">
                Operating in tenant
              </div>
              <div className="text-sm font-semibold truncate">{businessName}</div>
              <div className="text-xs text-slate-400 font-mono">{tenantId}</div>
            </div>

            {/* Tools */}
            <div className="flex-1 overflow-y-auto px-2 py-2">
              {/* Tenant switcher */}
              <div className="px-3 pt-2 pb-1 text-[11px] uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                <ArrowLeftRight className="w-3 h-3" />
                Switch tenant
              </div>
              {tenants === null ? (
                <div className="px-3 py-2 text-xs text-slate-500">Loading tenants…</div>
              ) : tenants.length === 0 ? (
                <div className="px-3 py-2 text-xs text-slate-500">
                  No tenants available for this account.
                </div>
              ) : (
                tenants.map((t) => {
                  const isCurrent = t.slug === tenantId;
                  return (
                    <button
                      key={t.slug}
                      onClick={() => switchTenant(t.slug)}
                      className={`${rowCls} w-full text-left ${
                        isCurrent ? "bg-slate-800" : ""
                      }`}
                      aria-current={isCurrent ? "true" : undefined}
                    >
                      <span className="flex-1 min-w-0">
                        <span className="block truncate">{t.name || t.slug}</span>
                        <span className="block text-[11px] text-slate-400 font-mono truncate">
                          {t.slug}
                          {t.status && t.status !== "active" ? ` · ${t.status}` : ""}
                        </span>
                      </span>
                      {isCurrent && <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />}
                    </button>
                  );
                })
              )}

              <div className="px-3 pt-4 pb-1 text-[11px] uppercase tracking-wider text-slate-400">
                This tenant
              </div>
              {tenantTools.map((tool) => (
                <Link
                  key={tool.to}
                  to={tool.to}
                  className={rowCls}
                  onClick={() => onClose()}
                >
                  <span className="text-slate-400">{tool.icon}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block">{tool.label}</span>
                    {tool.hint && (
                      <span className="block text-[11px] text-slate-400 truncate">
                        {tool.hint}
                      </span>
                    )}
                  </span>
                </Link>
              ))}

              <div className="px-3 pt-4 pb-1 text-[11px] uppercase tracking-wider text-slate-400">
                Platform
              </div>
              <a
                href="https://manage.clubcentral.app"
                target="_blank"
                rel="noopener noreferrer"
                className={rowCls}
                onClick={() => onClose()}
              >
                <span className="text-slate-400">
                  <Building2 className="w-4 h-4" />
                </span>
                <span className="flex-1">ClubCentral Manager</span>
                <ExternalLink className="w-3.5 h-3.5 text-slate-500" />
              </a>
            </div>

            {/* Diagnostics footer */}
            <div className="px-4 py-3 border-t border-slate-700 text-[11px] text-slate-400 space-y-0.5">
              <div className="flex items-center gap-1.5">
                <Activity className="w-3 h-3" />
                <span className="font-mono truncate" title={apiConfig.BASE_URL}>
                  {apiConfig.BASE_URL}
                </span>
              </div>
              {currentVersion && (
                <div
                  className="font-mono"
                  title={`Build: ${currentVersion.buildId}`}
                >
                  build {currentVersion.gitHash}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default CCManagementDrawer;
